import {
    AfterViewInit,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChange,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import {InsightAnalysis, InsightLineItem, InsightRow, SymbolIconInfo} from '@models/insights';
import {deepCopy} from "@utils/functions";
import {DEFAULT_CURRENCY, TEXT_FORMAT_TYPES} from "@utils/constants";
import {InsightsService} from "@services/insights.service";
import {finalize, map} from "rxjs/operators";
import {AlertService} from "@services/alert.service";
import {NumberFormattingService} from "@services/number-formatting.service";
import {Subscription} from "rxjs";
import {AutoUnsubscribe} from '../../../decorators/auto-unsubscribe';
import {DataView} from "@models/dataview";
import {Footnote} from "@models/footnote";
import {StepLabelService} from '@services/step-label.service';

declare var bootstrap: any;

interface BuildRowsState {
    pendingSymbols: string[];
    nestingLevel: number;
    primaryDivisionFound: boolean;
    divisionSeparatorIndex: number;
    isMainRowProcessed: boolean;
    isFirstNumeratorRow: boolean;
    isFirstDenominatorRow: boolean;
    openingParenthesisStack: Array<{ pairId: number, rowIndex: number, symbolIndex: number, nestingLevel: number }>;
}

@Component({
    selector: 'app-insights-v2',
    templateUrl: './insights-v2.component.html',
    styleUrls: ['./insights-v2.component.scss'],
    standalone: false
})
@AutoUnsubscribe('subsArr$')
export class InsightsV2Component implements OnInit, AfterViewInit, OnDestroy, OnChanges {
    @Input() activeDataView: DataView;
    @Input() companyId: number;
    @Input() visibleColumnMetadata: any[];
    @Input() currency: string = DEFAULT_CURRENCY;
    @Input() showCurrency: boolean = false;
    @Input() insightEquationComponents: InsightAnalysis = null;
    @Input() financialsTableSelector: string = 'app-financials-table table.table';
    @Input() showHidden: boolean = false;
    @Input() commonSizeDecimalPrecision: number = 1;
    @Input() showTrend: boolean = false;
    @Input() showCommonSize: boolean = false;
    @Input() showLoadingView: boolean = false;
    @Input() multiplyByFactor: string = 'ACTUALS';
    @Output() closeInsights = new EventEmitter<boolean>();
    @Output() openSourceBox = new EventEmitter<Footnote>();

    @ViewChild('insightsTable', {static: false}) insightsTable: ElementRef;
    @ViewChild('insightsScrollContainer', {static: false}) insightsScrollContainer: ElementRef;

    subsArr$: Subscription[] = [];
    hasHorizontalScrollbar: boolean = false;
    private resizeObserver: ResizeObserver | null = null;
    private financialsResizeObserver: ResizeObserver | null = null;

    insightRows: InsightRow[] = [];
    mainRow: InsightRow;
    
    // Parentheses tracking
    private parenthesesPairs: Map<number, { openingRowIndex: number, closingRowIndex: number, containedRowIndices: number[] }> = new Map();
    private nextParenthesisPairId: number = 1;
    private highlightedParenthesisPairId: number | null = null;

    get totalVisibleColumnCount(): number {
        if (!this.activeDataView?.columns) {
            return 1;
        }
        let count = 1; // Label column
        for (const column of this.activeDataView.columns) {
            if (this.showHidden || !column.isHidden) {
                count++; // Value column
                if (!column.trendIsHidden) {
                    count++; // Trend column
                }
                if (!column.commonSizeIsHidden) {
                    count++; // Common size column
                }
            }
        }
        return count;
    }

    private dynamicStyleElement: HTMLStyleElement | null = null;
    private readonly symbolIconMap: Map<string, SymbolIconInfo> = new Map([
        ['+', {iconName: 'fa-plus', iconColor: '#1D6E2A', backgroundColor: 'rgba(187, 231, 193, .5)'}],
        ['-', {iconName: 'fa-minus', iconColor: '#D10E0E', backgroundColor: 'rgba(250, 228, 228, 1)'}],
        ['*', {iconName: 'fa-xmark', iconColor: '#700ED1', backgroundColor: 'rgba(112, 14, 209, .11)'}],
        ['/', {iconName: 'fa-divide', iconColor: '#C10007', backgroundColor: 'rgba(249, 96, 0, .125)'}],
        ['u-', {iconName: 'fa-minus', iconColor: '#464646', backgroundColor: 'transparent', customClass: 'invert-value-icon'}],
        // This transparent icon is intentionally hidden to create space for alignment. We may decide to show these
        // leading equals symbols in the future, and can do so by simply changing the colors here.
        ['=', {iconName: 'fa-equals', iconColor: 'transparent', backgroundColor: 'transparent'}],
    ]);

    excludedCellTooltipText: string;

    constructor(
        private _insightsService: InsightsService,
        private alertService: AlertService,
        private _numberFormattingService: NumberFormattingService,
        private _stepLabelService: StepLabelService,
    ) {
    }

    ngOnInit() {
        const spreadStep = this._stepLabelService.getStepLabel('SPREAD_FORMAL');
        this.excludedCellTooltipText = `This item is hidden because it was excluded during the ${spreadStep} step and does not affect calculations.`;
        if (this.insightEquationComponents) {
            this._resetInsightRows(this.insightEquationComponents);
        }
    }

    ngOnChanges(changes: SimpleChanges) {
        const insightEquationComponentsChanges: SimpleChange = changes['insightEquationComponents'];
        const showCurrencyChanges: SimpleChange = changes['showCurrency'];
        const showTrendChanges: SimpleChange = changes['showTrend'];
        const showCommonSizeChanges: SimpleChange = changes['showCommonSize'];
        const multiplyByFactorChanges: SimpleChange = changes['multiplyByFactor'];
        
        if (insightEquationComponentsChanges?.isFirstChange() || !this.insightEquationComponents){
            return
        }
        if (insightEquationComponentsChanges) {
            this._resetInsightRows(this.insightEquationComponents);
            this.syncColumnWidths();
        } else if (showCurrencyChanges || multiplyByFactorChanges) {
            this._reformatExistingInsightRows();
            this.syncColumnWidths();
        }
        
        if (showTrendChanges || showCommonSizeChanges) {
            setTimeout(() => this.syncColumnWidths());
        }
    }

    ngAfterViewInit() {
        this.syncColumnWidths();
        this._setupScrollbarDetection();
        this.enableTooltips();
    }

    private _setupScrollbarDetection(): void {
        if (!this.insightsScrollContainer?.nativeElement) {
            return;
        }

        const container = this.insightsScrollContainer.nativeElement;

        // Initial check
        this._checkHorizontalScrollbar();

        // Set up ResizeObserver to detect when content changes
        this.resizeObserver = new ResizeObserver(() => {
            this._checkHorizontalScrollbar();
        });
        this.resizeObserver.observe(container);

        // Also observe the table for content changes
        if (this.insightsTable?.nativeElement) {
            this.resizeObserver.observe(this.insightsTable.nativeElement);
        }

        const financialsTable = document.querySelector(this.financialsTableSelector);
        if (financialsTable) {
            this.financialsResizeObserver = new ResizeObserver(() => {
                this.syncColumnWidths();
            });
            this.financialsResizeObserver.observe(financialsTable);
        }
    }

    private _checkHorizontalScrollbar(): void {
        if (!this.insightsScrollContainer?.nativeElement) {
            return;
        }
        const container = this.insightsScrollContainer.nativeElement;
        this.hasHorizontalScrollbar = container.scrollWidth > container.clientWidth;
    }
   
    enableTooltips() {
        // Bootstrap tooltip initialization
        const tooltipTriggerList = [].slice.call(document.querySelectorAll(':scope #insights-scroll-container [data-bs-toggle="tooltip"]'));
        tooltipTriggerList.forEach(tooltipTriggerEl => {
            return new bootstrap.Tooltip(tooltipTriggerEl)
        })
    }

    ngOnDestroy() {
        this._removeDynamicStyle();
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.financialsResizeObserver) {
            this.financialsResizeObserver.disconnect();
            this.financialsResizeObserver = null;
        }
    }

    handleCloseInsights() {
        this.closeInsights.emit(true);
    }
    
    handleComponentLabelClick(component: InsightRow) {
        if (component.isRaw || component.isSymbol) {
            return;
        }
        if (component.childRows && component.childRows.length > 0) {
            this.clickCloseDetail(component);
        } else {
            this.clickOpenDetail(component);
        }
    }

    clickOpenDetail(component: InsightRow): void {
        component.loadingChildRows = true;
        const payload = {
            lineItemRef: component.ref,
            companyId: this.companyId,
            visibleColumnMetadata: this.markExcludedCellsInColumnMetadata(component, this.visibleColumnMetadata)
        };
        this.subsArr$.push(this._insightsService.getInsightForItem(payload)
            .pipe(
                map(data => data.response.objects[0]))
            .pipe(
                finalize(() => {component.loadingChildRows = false;}))
            .subscribe(
                response => {
                    if (response.components.length > 2) {
                        const parentNestingLevel = component.parenthesisNestingLevel || 0;
                        component.childRows = this._buildInsightRows(response.components.slice(1), false, parentNestingLevel);
                        this.parenthesesPairs.clear();
                        this._buildParenthesesPairsMapping();
                        this._updateRowHighlightingStates();
                        setTimeout(() => this.enableTooltips());
                    }
                }, (err) => {
                    console.error('Error fetching insight detail: ', err);
                    this.alertService.error('Error fetching insight detail');
                }
            )
        );
    }
    
    markExcludedCellsInColumnMetadata(component: InsightLineItem, visibleColumnMetadata: any[]): any[] {
        const visibleColumnMetadataCopy = deepCopy(visibleColumnMetadata);
        if (!component.values.some(val => val?.isExcludedFromCalculations)) {
            return visibleColumnMetadataCopy
        }
        for (let i=0; i<component.values.length; i++) {
            if (component?.values?.[i]?.isExcludedFromCalculations) {
                try {
                    visibleColumnMetadataCopy[i]['isExcludedFromCalculations'] = true;
                } catch (e) {
                    console.warn('Failed to hide excluded items in child rows', e)
                }
            }
        }
        return visibleColumnMetadataCopy
    }

    clickCloseDetail(component: InsightRow): void {
        component.childRows = [];
        this._updateRowHighlightingStates();
    }

    _reformatExistingInsightRows(): void {
        if (this.mainRow) {
            this._formatCellValues(this.mainRow);
        }
        this._reformatRowsRecursive(this.insightRows);
    }

    private _reformatRowsRecursive(rows: InsightRow[]): void {
        rows.forEach(row => {
            this._formatCellValues(row);
            if (row.childRows?.length) {
                this._reformatRowsRecursive(row.childRows);
            }
        });
    }
    
    private _resetInsightRows(insight: InsightAnalysis): void {
        this.insightRows = [];
        this.parenthesesPairs.clear();
        this.nextParenthesisPairId = 1;
        this.highlightedParenthesisPairId = null;

        // Check if the main item is a ratio - division separator only applies to ratios
        const isRatio = insight.item?.textFormat === TEXT_FORMAT_TYPES.RATIO;
        const updatedRows: InsightRow[] = this._buildInsightRows(insight.components, isRatio);
        this.mainRow = updatedRows[0];
        this.insightRows = updatedRows.slice(1);
        
        this._buildParenthesesPairsMapping();
        this._updateRowHighlightingStates();
    }

    private _buildInsightRows(components: Array<InsightLineItem>, isRatio: boolean = false, parentNestingLevel: number = 0): InsightRow[] {
        const rows: InsightRow[] = [];

        const state: BuildRowsState = {
            pendingSymbols: [],
            nestingLevel: parentNestingLevel,
            primaryDivisionFound: false,
            divisionSeparatorIndex: -1,
            isMainRowProcessed: false,
            isFirstNumeratorRow: true,
            isFirstDenominatorRow: true,
            openingParenthesisStack: []
        };

        components.forEach((component: InsightLineItem, index: number) => {
            const componentCopy: InsightRow = deepCopy(component);

            if (componentCopy.isSymbol) {
                this._processSymbolComponent(componentCopy, index, state, rows, isRatio);
            } else {
                this._processNonSymbolComponent(componentCopy, state, rows);
            }
        });

        if (state.divisionSeparatorIndex > -1) {
            rows.forEach((row, idx) => {
                if (row.isDivisionSeparator) {
                    return;
                }
                row.divisionGroup = idx < state.divisionSeparatorIndex ? 'numerator' : 'denominator';
            });
        }

        return rows;
    }
    
    private _buildParenthesesPairsMapping(): void {
        const allRowsFlat = this.getAllRowsFlatWithChildren();
        
        allRowsFlat.forEach((row, index) => {
            if (row.isOpeningParenthesis && row.parenthesisPairId) {
                let closingRowIndex = -1;
                for (let i = index + 1; i < allRowsFlat.length; i++) {
                    if (allRowsFlat[i].parenthesisPairId === row.parenthesisPairId && allRowsFlat[i].isClosingParenthesis) {
                        closingRowIndex = i;
                        break;
                    }
                }
                
                if (closingRowIndex > -1) {
                    const containedRowIndices: number[] = [];
                    for (let i = index + 1; i < closingRowIndex; i++) {
                        containedRowIndices.push(i);
                    }
                    
                    this.parenthesesPairs.set(row.parenthesisPairId, {
                        openingRowIndex: index,
                        closingRowIndex: closingRowIndex,
                        containedRowIndices: containedRowIndices
                    });
                }
            }
        });
    }

    private _processSymbolComponent(
        componentCopy: InsightRow,
        index: number,
        state: BuildRowsState,
        rows: InsightRow[],
        isRatio: boolean
    ): void {
        // Skip leading '=' from API; first line now uses explicit '+' for alignment, so '=' would only cause offset
        if ([0, 1].includes(index) && componentCopy.label === '=') {
            return;
        }
        
        if (componentCopy.label === 'u+') {
            // explicitly remove unary plus symbols from display
            return
        }

        // Track nesting level for parentheses
        if (componentCopy.label === '(') {
            const nestingLevel = state.nestingLevel;
            state.nestingLevel++;
            state.pendingSymbols.push(componentCopy.label);
            const pairId = this.nextParenthesisPairId++;
            const symbolIndex = state.pendingSymbols.length - 1;
            state.openingParenthesisStack.push({ pairId, rowIndex: rows.length, symbolIndex, nestingLevel });
            return;
        }

        if (componentCopy.label === ')') {
            const nestingLevel = state.nestingLevel;
            state.nestingLevel--;
            if (state.openingParenthesisStack.length > 0) {
                const openingInfo = state.openingParenthesisStack.pop()!;
                this._appendClosingParenthesis(rows, openingInfo.pairId, nestingLevel);
            } else {
            this._appendClosingParenthesis(rows);
            }
            return;
        }

        // Check for primary division: first '/' at nesting level 0 (only create separator for ratios)
        if (componentCopy.label === '/' && state.nestingLevel === 0 && !state.primaryDivisionFound) {
            state.primaryDivisionFound = true;
            if (isRatio) {
                // Create a division separator row only for ratios
                const separatorRow: InsightRow = {
                    id: -1,
                    label: '/',
                    ref: '',
                    values: [],
                    isSymbol: true,
                    isRaw: false,
                    baseLabel: '',
                    displayLabel: '',
                    symbolDisplayArray: [],
                    isDivisionSeparator: true
                };
                rows.push(separatorRow);
                state.divisionSeparatorIndex = rows.length - 1;
                return;
            } else {
                // For non-ratios, treat '/' as a regular symbol
                state.pendingSymbols.push(componentCopy.label);
                return;
            }
        }

        if (this._isNumericLabel(componentCopy.label)) {
            this._addComponentRow(componentCopy, state.pendingSymbols, rows, state);
            state.pendingSymbols.length = 0;
        } else {
            state.pendingSymbols.push(componentCopy.label);
        }
    }

    private _processNonSymbolComponent(
        componentCopy: InsightRow,
        state: BuildRowsState,
        rows: InsightRow[]
    ): void {
        // Skip alignment logic for the main row (first non-symbol component)
        if (!state.isMainRowProcessed) {
            state.isMainRowProcessed = true;
            this._addComponentRow(componentCopy, state.pendingSymbols, rows, state);
            state.pendingSymbols.length = 0;
            return;
        }

        this._addComponentRow(componentCopy, state.pendingSymbols, rows, state);
        if (!state.primaryDivisionFound) {
            state.isFirstNumeratorRow = false;
        } else {
            state.isFirstDenominatorRow = false;
        }
        state.pendingSymbols.length = 0;
    }

    private _appendClosingParenthesis(rows: InsightRow[], pairId?: number, nestingLevel?: number): void {
        if (rows.length === 0) {
            return;
        }

        const lastRow = rows[rows.length - 1];
        lastRow.baseLabel = `${lastRow.baseLabel} )`;
        if (pairId !== undefined) {
            lastRow.parenthesisPairId = pairId;
            lastRow.isClosingParenthesis = true;
        }
        if (nestingLevel !== undefined) {
            lastRow.parenthesisNestingLevel = nestingLevel;
        }
        this._updateDisplayLabel(lastRow);
    }

    /** Symbols that display as an operator icon (take space); '=' is excluded (invisible placeholder). */
    private static readonly DISPLAY_OPERATOR_SYMBOLS = ['+', '-', '*', '/', 'u-'] as const;

    private _addComponentRow(componentCopy: InsightRow, pendingSymbols: string[], rows: InsightRow[], state?: BuildRowsState): void {
        if (state?.isMainRowProcessed) {
            const isFirstLine = rows.length <= 1 || (state.primaryDivisionFound && state.isFirstDenominatorRow);
            const hasLeadingOperator = pendingSymbols.length > 0 && InsightsV2Component.DISPLAY_OPERATOR_SYMBOLS.includes(pendingSymbols[0] as any);
            const labelStartsWithOperator = this._labelStartsWithOperator(componentCopy.label);
            if (isFirstLine && !hasLeadingOperator && !labelStartsWithOperator) {
                pendingSymbols.unshift('+');
            }
            else if (isFirstLine && hasLeadingOperator && pendingSymbols[0] == 'u-') {
                pendingSymbols.unshift('+');
            } else if (!isFirstLine && !hasLeadingOperator && pendingSymbols[0] !== '=') {
                pendingSymbols.unshift('=');
            }
        }
        this._processComponentWithSymbols(componentCopy, pendingSymbols);
        this._formatCellValues(componentCopy);
        const rowIndex = rows.length;
        
        if (state) {
            componentCopy.parenthesisNestingLevel = state.nestingLevel;
            
            state.openingParenthesisStack.forEach(info => {
                if (info.rowIndex === rowIndex) {
                    componentCopy.parenthesisPairId = info.pairId;
                    componentCopy.isOpeningParenthesis = true;
                    componentCopy.parenthesisNestingLevel = info.nestingLevel;
                    // Find '(' in final array (may have shifted due to prepended '=' for alignment)
                    const foundIndex = pendingSymbols.indexOf('(', info.symbolIndex);
                    componentCopy.openingParenthesisSymbolIndex = foundIndex !== -1 ? foundIndex : info.symbolIndex;
                }
            });
        }
        
        rows.push(componentCopy);
    }

    private _processComponentWithSymbols(componentCopy: InsightRow, pendingSymbols: string[]): void {
        componentCopy.baseLabel = componentCopy.label;
        componentCopy.symbolDisplayArray = pendingSymbols.map((symbol: string) => {
            return {
            symbol,
            isIcon: this.symbolIconMap.has(symbol),
            iconInfo: this.symbolIconMap.get(symbol) || null
            };
        });
        this._updateDisplayLabel(componentCopy);
    }

    private _updateDisplayLabel(componentCopy: InsightRow): void {
        if (componentCopy.symbolDisplayArray.length > 0) {
            const symbolPrefixString = componentCopy.symbolDisplayArray.map(s => s.symbol).join(' ');
            componentCopy.displayLabel = `${symbolPrefixString} ${componentCopy.baseLabel}`;
        } else {
            componentCopy.displayLabel = componentCopy.baseLabel;
        }
    }

    private _isNumericLabel(label: string): boolean {
        if (!label?.trim()) {
            return false;
        }
        const num = parseFloat(label);
        return !isNaN(num) && isFinite(num);
    }

    /** True if the label already starts with an operator (+, -, *, /) so we should not prepend another. */
    private _labelStartsWithOperator(label: string): boolean {
        if (!label?.trim()) {
            return false;
        }
        const trimmed = label.trim();
        let pos = 0;
        if (trimmed.charAt(pos) === '(') {
            pos++;
        }
        while (pos < trimmed.length && trimmed.charAt(pos) === ' ') {
            pos++;
        }
        const firstChar = pos < trimmed.length ? trimmed.charAt(pos) : '';
        return firstChar === '+' || firstChar === '-' || firstChar === '*' || firstChar === '/';
    }

    private syncColumnWidths(): void {
        const financialsTable = document.querySelector(this.financialsTableSelector) as HTMLTableElement;
        const insightsTableElement = this.insightsTable?.nativeElement as HTMLTableElement;

        if (!financialsTable || !insightsTableElement) {
            return;
        }

        const insightsHeaderRow = insightsTableElement.querySelector('thead tr:first-child');

        if (!insightsHeaderRow) {
            return;
        }

        const financialsRow =
            financialsTable.querySelector('tbody tr') ??
            financialsTable.querySelector('thead tr:first-child');

        if (!financialsRow) {
            return;
        }

        const financialsCells = financialsRow.querySelectorAll('th:not(.hidden), td:not(.hidden)');
        const insightsCells = insightsHeaderRow.querySelectorAll('th:not(.hidden)');

        if (insightsCells.length === 0 || financialsCells.length === 0) {
            return;
        }

        const columnWidths: string[] = [];
        const labelWidth = financialsCells[0].getBoundingClientRect().width;
        columnWidths.push(`${labelWidth}px`);

        for (let i = 1; i < insightsCells.length; i++) {
            try {
                const width = financialsCells[i].getBoundingClientRect().width;
                columnWidths.push(`${width}px`);
            } catch (e) {
                columnWidths.push('110px');
            }
        }

        this._updateDynamicStyles(columnWidths, insightsTableElement);
    }

    private _updateDynamicStyles(columnWidths: string[], insightsTableElement: HTMLTableElement): void {
        if (!this.dynamicStyleElement) {
            this.dynamicStyleElement = document.createElement('style');
            this.dynamicStyleElement.id = 'insights-table-dynamic-styles';
            document.head.appendChild(this.dynamicStyleElement);
        }

        let cssRules = '';
        for (let i = 0; i < columnWidths.length; i++) {
            const defaultWidth = i === 0 ? '310px' : '110px';
            const width = columnWidths[i];
            cssRules += `
                .insights-table th:nth-child(${i + 1}),
                .insights-table td:nth-child(${i + 1}) {
                    width: var(--insights-col-${i}-width, ${defaultWidth});
                    min-width: var(--insights-col-${i}-width, ${defaultWidth});
                    max-width: var(--insights-col-${i}-width, ${defaultWidth});
                }
            `;
            insightsTableElement.style.setProperty(`--insights-col-${i}-width`, width);
        }

        this.dynamicStyleElement.textContent = cssRules;
    }

    private _removeDynamicStyle(): void {
        if (this.dynamicStyleElement) {
            this.dynamicStyleElement.remove();
            this.dynamicStyleElement = null;
        }
    }

    _formatCellValues(component: InsightRow): void {
        component.values.forEach(valueObj => {
            valueObj.formattedValue = this.formatValue(valueObj.value, component.textFormat);
            if (![null, undefined].includes(valueObj?.trendValue)) {
                try {
                    valueObj.formattedTrendValue = (valueObj.trendValue * 100).toFixed(1);
                } catch (e) {
                    console.error('Error formatting trend value: ', e);
                }
            }
            if (valueObj?.commonSizedValue) {
                valueObj.formattedCommonSizedValue = this.formatValue(valueObj.commonSizedValue, TEXT_FORMAT_TYPES.PERCENT);
            }
        });
    }

    formatValue(value: any, textFormat: string): string {
        let formattedValue: string;
        const options = {multiply_by_factor: this.multiplyByFactor, showDollarSign: this.showCurrency, currency: this.currency};
        if (textFormat === TEXT_FORMAT_TYPES.CURRENCY) {
            const numDecimals = 0;
            formattedValue = this._numberFormattingService.formatNumber(value, textFormat, numDecimals, options);
        } else {
            formattedValue = this._numberFormattingService.formatNumber(value, textFormat, null, options);
        }
        return formattedValue;
    }

    clickOpenSourceBox(sourceBox: Footnote) {
        if (sourceBox !== null) {
            this.openSourceBox.emit(sourceBox);
        }
    }
    
    onParenthesisHover(pairId: number | undefined): void {
        this.highlightedParenthesisPairId = pairId || null;
        this._updateRowHighlightingStates();
    }
    
    onParenthesisLeave(): void {
        this.highlightedParenthesisPairId = null;
        this._updateRowHighlightingStates();
    }
    
    onSymbolMouseEnter(symbol: string, component: InsightRow, symbolIdx: number): void {
        if (symbol === '(' && component.isOpeningParenthesis && 
            symbolIdx === component.openingParenthesisSymbolIndex) {
            this.onParenthesisHover(component.parenthesisPairId);
        } else if (symbol === ')' && component.isClosingParenthesis) {
            this.onParenthesisHover(component.parenthesisPairId);
        }
    }
    
    onSymbolMouseLeave(symbol: string): void {
        if (symbol === '(' || symbol === ')') {
            this.onParenthesisLeave();
        }
    }
    
    private _updateRowHighlightingStates(): void {
        const allRowsWithChildren = this.getAllRowsFlatWithChildren();
        const pairId = this.highlightedParenthesisPairId;
        
        const rowIndexMap = new Map<InsightRow, number>();
        allRowsWithChildren.forEach((row, index) => {
            rowIndexMap.set(row, index);
        });
        
        this._buildParentChildMap();
        
        const resetHighlighting = (row: InsightRow) => {
            row.isHighlightedOpening = false;
            row.isHighlightedContained = false;
            row.isHighlightedClosing = false;
            row.isParenthesisSymbolHighlighted = false;
        };
        
        if (pairId === null) {
            allRowsWithChildren.forEach(resetHighlighting);
            return;
        }
        
        const pair = this.parenthesesPairs.get(pairId);
        if (!pair) {
            allRowsWithChildren.forEach(resetHighlighting);
            return;
        }
        
        const containedRowIndicesSet = new Set(pair.containedRowIndices);
        
        allRowsWithChildren.forEach(row => {
            resetHighlighting(row);
            
            const rowIndex = rowIndexMap.get(row);
            
            if (rowIndex === undefined) {
                return;
            }
            
            if (rowIndex === pair.openingRowIndex) {
                row.isHighlightedOpening = true;
                if (row.isOpeningParenthesis) {
                    row.isParenthesisSymbolHighlighted = true;
                }
            } else if (rowIndex === pair.closingRowIndex) {
                row.isHighlightedClosing = true;
                if (row.isClosingParenthesis) {
                    row.isParenthesisSymbolHighlighted = true;
                }
            } else if (containedRowIndicesSet.has(rowIndex)) {
                row.isHighlightedContained = true;
            }
        });
    }
    
    getAllRowsFlat(): InsightRow[] {
        return [this.mainRow, ...this.insightRows].filter(row => row && !row.isDivisionSeparator);
    }
    
    private getAllRowsFlatWithChildren(): InsightRow[] {
        const result: InsightRow[] = [];
        
        const addRowsRecursive = (rows: InsightRow[]) => {
            rows.forEach(row => {
                if (row && !row.isDivisionSeparator) {
                    result.push(row);
                    if (row.childRows && row.childRows.length > 0) {
                        addRowsRecursive(row.childRows);
                    }
                }
            });
        };
        
        if (this.mainRow) {
            addRowsRecursive([this.mainRow]);
        }
        addRowsRecursive(this.insightRows);
        
        return result;
    }
    
    private _buildParentChildMap() {
        const addChildren = (rows: InsightRow[]) => {
            rows.forEach(row => {
                if (row && row.childRows && row.childRows.length > 0) {
                    row.childRows.forEach(child => {
                        if (child && !child.isDivisionSeparator) {
                            if (child.childRows && child.childRows.length > 0) {
                                addChildren(child.childRows);
                            }
                        }
                    });
                }
            });
        };
        
        if (this.mainRow) {
            addChildren([this.mainRow]);
        }
        addChildren(this.insightRows);
    }
}
